import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import { Building2, FileText, MapPin, Phone } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { StatusChip, useRecordVisit } from '@/design';
import { secAdvApi } from '../../api/sec-adv-api';
import type { IAdminSecAdvFirmDetail, ISecAdvCatalogue } from '../../api/sec-adv-contracts';
import {
  LoadError,
  AdminPage,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { firmCardPath, firmTabPath, type FirmTab } from '../../components/firm-link';
import { openedFromResults, originOf, searchFrom } from '../../components/participant-link';
import { TEAM_PATHS } from '../../paths';
import { formatCount, formatDollars, useSecAdvCatalogue } from '../../components/sec-adv-values';
import { FirmAffiliates } from './firm-affiliates';
import { FirmAnalytics } from './firm-analytics';
import { figuresOf, type IFirmFigures } from './firm-data';
import { FirmFilings } from './firm-filings';
import { FirmInternal } from './firm-internal';
import { FirmOverview } from './firm-overview';
import { Dash, ERA_NOTE, SourceLink, lastListPath } from './register';

const TABS: { tab: FirmTab; label: string }[] = [
  { tab: 'overview', label: 'Overview' },
  { tab: 'analytics', label: 'Analytics' },
  { tab: 'filings', label: 'Filings' },
  { tab: 'affiliates', label: 'Affiliates' },
  { tab: 'internal', label: 'Internal' }
];

/**
 * One firm of the SEC ADV register (SR-423, modelled on RIA Pulse's firm page) —
 * `GET /api/v1/admin/sec-adv/firms/{firmCrd}`, read once and shared by its five tabs, each its own
 * address: Overview at the firm's, then `/analytics`, `/filings` (`?item=` scrolls to one answer),
 * `/affiliates` and `/internal`. Full width; the header band is the same on every tab — the name
 * and who the firm is on the left, its size on the right, the tabs along its edge. The breadcrumb
 * returns to the list as it was last left.
 */
export function AdminSecAdvFirmPage() {
  const { firmCrd = '', tab } = useParams();
  return <FirmPage key={firmCrd} firmCrd={firmCrd} tab={tab ?? 'overview'} />;
}

function FirmPage({ firmCrd, tab }: { firmCrd: string; tab: string }) {
  const { data, error } = useAdminData<{ firm: Nullable<IAdminSecAdvFirmDetail> }>(
    async (signal) => ({ firm: await secAdvApi.getFirm(firmCrd, signal) }),
    [firmCrd]
  );
  const labels = useSecAdvCatalogue();
  const firm = data?.firm ?? null;
  const figures = React.useMemo(() => firm && figuresOf(firm, labels.data), [firm, labels.data]);
  useRecordVisit(
    firm ? { kind: 'register-firm', path: firmCardPath(firm.firmCrd), label: firm.legalName } : null
  );

  const name = firm?.legalName ?? 'Firm';
  const { state } = useLocation();
  const origin = originOf(state);
  return (
    <AdminPage
      title={name}
      crumb={
        <>
          {/* Opened beside another list (SR-530) or from the search results page (SR-463), the
              way back is there. */}
          {origin ? (
            <Link to={origin.back} className="text-[var(--color-sapphire)] hover:underline">
              {origin.title}
            </Link>
          ) : openedFromResults(state) ? (
            <Link
              to={`${TEAM_PATHS.search}${searchFrom(state)}`}
              className="text-[var(--color-sapphire)] hover:underline"
            >
              Search results
            </Link>
          ) : (
            <Link to={lastListPath()} className="text-[var(--color-sapphire)] hover:underline">
              SEC ADV register
            </Link>
          )}
          <span aria-hidden> › </span>
          {name}
        </>
      }
      actions={
        <div className="grid justify-items-end gap-3">
          {firm && figures && <FirmStats firm={firm} figures={figures} />}
        </div>
      }
      lede={firm && figures && <FirmFacts firm={firm} figures={figures} />}
      tabs={
        firm
          ? TABS.map((entry) => ({
              to: firmTabPath(firm.firmCrd, entry.tab),
              label: entry.label,
              end: true
            }))
          : undefined
      }
    >
      {error && data === null && <LoadError message={error} />}
      {data === null ? (
        error ? null : <TableSkeleton rows={4} />
      ) : firm && figures ? (
        <>
          {labels.error && (
            <Alert>
              <AlertTitle>Labels could not be loaded</AlertTitle>
              <AlertDescription>The read failed: {labels.error}</AlertDescription>
            </Alert>
          )}
          <FirmTabBody
            tab={tab}
            firm={firm}
            figures={figures}
            catalogue={labels.data}
            catalogueRead={labels.data !== null || labels.error !== null}
          />
        </>
      ) : (
        <Alert>
          <AlertTitle>No such firm</AlertTitle>
          <AlertDescription>
            No firm in the register has CRD <strong>{firmCrd}</strong>.
          </AlertDescription>
        </Alert>
      )}
    </AdminPage>
  );
}

function FirmTabBody({
  tab,
  firm,
  figures,
  catalogue,
  catalogueRead
}: {
  tab: string;
  firm: IAdminSecAdvFirmDetail;
  figures: IFirmFigures;
  catalogue: Nullable<ISecAdvCatalogue>;
  catalogueRead: boolean;
}) {
  switch (tab) {
    case 'overview':
      return <FirmOverview firm={firm} figures={figures} catalogue={catalogue} />;
    case 'analytics':
      return <FirmAnalytics figures={figures} />;
    case 'filings':
      return catalogueRead ? (
        <FirmFilings firm={firm} catalogue={catalogue} />
      ) : (
        <TableSkeleton rows={4} />
      );
    case 'affiliates':
      return <FirmAffiliates firm={firm} catalogue={catalogue} />;
    case 'internal':
      return <FirmInternal firm={firm} />;
    default:
      return (
        <p className="text-sm text-muted-foreground">
          This firm has no tab called “{tab}”.{' '}
          <Link to={firmCardPath(firm.firmCrd)} className="underline">
            Overview
          </Link>
        </p>
      );
  }
}

/** The header's facts: where the firm is, how to reach it, its numbers and its type. */
function FirmFacts({ firm, figures }: { firm: IAdminSecAdvFirmDetail; figures: IFirmFigures }) {
  const address = firm.summary.mainAddress;
  const place = [address?.city, address?.state].filter(Boolean).join(', ');
  const participants = firm.participants.length;
  return (
    // Spans, not a list: the header draws its lede as a paragraph.
    <span
      data-firm-facts
      className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[0.8125rem] text-foreground"
    >
      {place && (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <MapPin className="size-4 text-muted-foreground" aria-hidden />
          <SourceLink firmCrd={firm.firmCrd} source="MainAddr.City">
            {place}
          </SourceLink>
        </span>
      )}
      {address?.phone && (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <Phone className="size-4 text-muted-foreground" aria-hidden />
          <SourceLink firmCrd={firm.firmCrd} source="MainAddr.PhNb">
            {address.phone}
          </SourceLink>
        </span>
      )}
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <FileText className="size-4 text-muted-foreground" aria-hidden />
        <SourceLink firmCrd={firm.firmCrd} source="Info.SECNb">
          SEC #{firm.secNumber}
        </SourceLink>
      </span>
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <Building2 className="size-4 text-muted-foreground" aria-hidden />
        <SourceLink firmCrd={firm.firmCrd} source="Info.FirmCrdNb">
          CRD {firm.firmCrd}
        </SourceLink>
      </span>
      <span>
        <StatusChip>{figures.isEra ? 'Exempt reporting adviser' : 'SEC-registered'}</StatusChip>
      </span>
      {participants > 0 && (
        <span>
          <Link to={firmTabPath(firm.firmCrd, 'internal')}>
            <StatusChip tone="success" dot>
              {participants === 1
                ? 'Participant matched'
                : `${participants} participants matched`}
            </StatusChip>
          </Link>
        </span>
      )}
    </span>
  );
}

/** The header's three figures, at its right. */
function FirmStats({ firm, figures }: { firm: IAdminSecAdvFirmDetail; figures: IFirmFigures }) {
  return (
    <dl className="flex gap-x-8">
      <Stat label="Total AUM">
        {figures.totalAum !== null ? (
          <SourceLink firmCrd={firm.firmCrd} source="FormInfo.Part1A.Item5F.Q5F2C">
            {formatDollars(figures.totalAum)}
          </SourceLink>
        ) : (
          <Dash title={figures.isEra ? ERA_NOTE : undefined} />
        )}
      </Stat>
      <Stat label="Employees">
        {figures.employees !== null ? (
          <SourceLink firmCrd={firm.firmCrd} source="FormInfo.Part1A.Item5A.TtlEmp">
            {formatCount(figures.employees)}
          </SourceLink>
        ) : (
          <Dash />
        )}
      </Stat>
      <Stat label="Clients">
        {figures.clients !== null ? (
          <SourceLink firmCrd={firm.firmCrd} source="FormInfo.Part1A.Item5D">
            {formatCount(figures.clients)}
          </SourceLink>
        ) : (
          <Dash />
        )}
      </Stat>
    </dl>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid content-start justify-items-end gap-0.5 text-right">
      <dt className="text-[0.6875rem] tracking-wide whitespace-nowrap text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="text-lg font-semibold whitespace-nowrap tabular-nums text-[var(--color-royal-blue)]">
        {children}
      </dd>
    </div>
  );
}
